import { Link } from 'react-router-dom';
import type { Venue, WeddingEvent } from '../lib/content';
import { durationLabel, fmtTime, untilLabel } from '../lib/time';
import { IconPinSolid } from './Icon';
import { useIsDesktop } from '../lib/viewport';

export type EventState = 'past' | 'now' | 'next' | 'later';

export function stateOf(event: WeddingEvent, at: number): EventState {
  const start = Date.parse(event.start);
  const end = event.end ? Date.parse(event.end) : start + 2 * 3600_000;
  if (at >= end) return 'past';
  if (at >= start) return 'now';
  return 'later';
}

function badgeFor(state: EventState, event: WeddingEvent, at: number): string | null {
  if (state === 'now') return 'Happening now';
  if (state === 'next') {
    const until = untilLabel(event.start, at);
    return until ? `Next · ${until}` : 'Next';
  }
  return null;
}

interface Props {
  event: WeddingEvent;
  venue?: Venue;
  state: EventState;
  at: number;
  /** Hero is the dominant card on Now and at the head of a day; row is the list. */
  density: 'hero' | 'row';
}

/**
 * One event card, two densities (the shared component the requirements ask for).
 * The phone layout and the laptop layout genuinely differ — on a laptop the time
 * becomes a left-hand column — so they are two small branches rather than one
 * layout bent through a media query until neither reads well.
 */
export default function EventCard({ event, venue, state, at, density }: Props) {
  const desktop = useIsDesktop();
  const badge = badgeFor(state, event, at);
  const hero = density === 'hero';
  const dress = event.dressCode?.short;
  const time = fmtTime(event.start);
  const className = [
    'card', 'ev', hero ? 'ev-hero' : 'ev-row', state === 'past' ? 'ev-past' : '',
  ].filter(Boolean).join(' ');

  if (desktop) {
    return (
      <Link className={className} to={`/schedule/${event.id}`}>
        <div className="ev-when">
          <div className={hero ? 'ev-hero-time' : 'ev-row-time'}>{time}</div>
          {hero && durationLabel(event.start, event.end) && (
            <div className="ev-dur">{durationLabel(event.start, event.end)}</div>
          )}
        </div>
        <div className="ev-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className={hero ? 'ev-hero-name' : 'ev-row-name'}>{event.name}</span>
            {badge && <span className="tag tag-now">{badge}</span>}
          </div>
          <div className="ev-line">
            {venue?.name ?? 'Venue to be confirmed'}
            {dress ? ` · ${dress}` : ''}
            {state === 'past' ? ' · finished' : ''}
          </div>
        </div>
        {state !== 'past' && (
          <span className={hero ? 'btn btn-secondary' : 'btn btn-ghost'} style={{ flex: 'none' }}>Details</span>
        )}
      </Link>
    );
  }

  if (hero) {
    return (
      <Link className={className} to={`/schedule/${event.id}`}>
        <div className="ev-hero-top">
          {badge ? <span className="tag tag-now">{badge}</span> : <span />}
          <span className="ev-hero-time">{time}</span>
        </div>
        <div className="ev-hero-name">{event.name}</div>
        <div className="ev-venue">
          <IconPinSolid size={14} />
          {venue?.name ?? 'Venue to be confirmed'}
          {venue?.floor ? ` · ${venue.floor.toLowerCase()}` : ''}
        </div>
        {(dress || event.dressCode?.tags?.length) && (
          <div className="ev-tags">
            {dress && <span className="tag tag-accent-2">{dress}</span>}
            {event.dressCode?.tags?.map((t) => <span key={t} className="tag tag-outline">{t}</span>)}
          </div>
        )}
      </Link>
    );
  }

  return (
    <Link className={className} to={`/schedule/${event.id}`}>
      <div className="ev-row-top">
        <span className="ev-row-name">{event.name}</span>
        <span className="ev-row-time">{time}</span>
      </div>
      <div className="ev-row-venue">
        {venue?.name ?? 'Venue to be confirmed'}
        {state === 'past' ? ' · finished' : ''}
      </div>
      {dress && <div><span className="tag tag-neutral">{dress}</span></div>}
    </Link>
  );
}
